import type { ParticipantBehaviour, TableWidget } from '@piccolohealth/pbs-common';
import { ChakraV3, Empty, Spin, useDebouncedCallback } from '@piccolohealth/ui';
import { type DateTime, P } from '@piccolohealth/util';
import React from 'react';

import { createModal } from '../../../../components/generic/Modal';
import { useParticipantBehavioursRecordingsQuery } from '../../../../graphql/hooks/useParticipantQuery';
import { useAppContext } from '../../../../hooks/useAppContext';
import { useTable } from '../../hooks/useTable';
import { ParticipantWidgetTimeRangeControl } from '../ParticipantWidgetTimeRangeControl';
import { TableWidgetContent } from './TableWidgetContent';

interface Props {
  participantId: string;
  widget: TableWidget;
  startDate: DateTime;
  endDate: DateTime;
}

export const TableWidgetViewModal = createModal<Props>((props) => {
  const { modal, participantId, widget } = props;
  const { visible, hide, remove } = modal;
  const { organization } = useAppContext();
  const [startDate, setStartDate] = React.useState(props.startDate);
  const [endDate, setEndState] = React.useState(props.endDate);

  const onStartDateChange = useDebouncedCallback((date: DateTime) => {
    setStartDate(date);
  }, 100);

  const onEndDateChange = useDebouncedCallback((date: DateTime) => {
    setEndState(date);
  }, 100);

  const participantBehaviourQuery = useParticipantBehavioursRecordingsQuery({
    organizationId: organization.id,
    participantId,
  });

  const participantBehaviours =
    (participantBehaviourQuery.data?.organization?.participant
      ?.behaviours as ParticipantBehaviour[]) ?? [];

  const table = useTable({
    participantBehaviours,
    widget,
    startDate,
    endDate,
    setWidget: P.noop,
  });

  const content = P.run(() => {
    if (participantBehaviourQuery.isLoading) {
      return <Spin />;
    }

    if (!participantBehaviours) {
      return <Empty />;
    }

    return (
      <ChakraV3.Stack w='full' h='full'>
        <ChakraV3.FormField label='Date'>
          <ParticipantWidgetTimeRangeControl
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={onStartDateChange}
            onEndDateChange={onEndDateChange}
          />
        </ChakraV3.FormField>
        <TableWidgetContent table={table} />
      </ChakraV3.Stack>
    );
  });

  return (
    <ChakraV3.Dialog.Root
      open={visible}
      onOpenChange={hide}
      onExitComplete={remove}
      size='cover'
      scrollBehavior='inside'
    >
      <ChakraV3.Dialog.Backdrop />
      <ChakraV3.Dialog.Positioner>
        <ChakraV3.Dialog.Content>
          <ChakraV3.DialogTitleSubtitle title={widget.title} />
          <ChakraV3.Dialog.Body>{content}</ChakraV3.Dialog.Body>
          <ChakraV3.Dialog.Footer>
            <ChakraV3.Button onClick={hide} size='sm' colorPalette='brand'>
              Close
            </ChakraV3.Button>
          </ChakraV3.Dialog.Footer>
        </ChakraV3.Dialog.Content>
      </ChakraV3.Dialog.Positioner>
    </ChakraV3.Dialog.Root>
  );
});
